// Searching our supplement list for the checker's supplements field
// (STACK-587). Only supplements in the list can be picked: the route checks
// keys from `backend/config/supplements.json` and nothing else (design doc
// §3.1, "Client-chosen supplements").
//
// It matches a supplement's `name` and its `key`, ignoring case and
// punctuation, so "st johns" finds "St. John's Wort" and "omega 3" finds
// `omega_3`. Matches rank name-start, then word-start, then anywhere, and
// alphabetically within a rank.

export const SUPPLEMENT_SEARCH_MIN_TERM = 2;
export const SUPPLEMENT_SEARCH_LIMIT = 8;

// Lowercase, punctuation dropped, separators to single spaces:
// "St. John's Wort" → "st johns wort", "omega_3" → "omega 3".
export const normalizeForSearch = (text) => String(text || '')
  .toLowerCase()
  .replace(/['’.]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const rankOf = (haystack, needle) => {
  if (!haystack || !needle) return null;
  if (haystack.startsWith(needle)) return 0;
  if (haystack.includes(` ${needle}`)) return 1;
  if (haystack.includes(needle)) return 2;
  return null;
};

/**
 * @param {Array<{key: string, name: string}>} supplements - our list
 * @param {string} term - what the visitor typed
 * @returns {Array<{key: string, name: string}>} the best matches, at most
 *   SUPPLEMENT_SEARCH_LIMIT; empty below SUPPLEMENT_SEARCH_MIN_TERM characters
 */
export const searchSupplements = (supplements, term) => {
  const needle = normalizeForSearch(term);
  if (needle.length < SUPPLEMENT_SEARCH_MIN_TERM || !Array.isArray(supplements)) return [];
  const ranked = [];
  supplements.forEach((s) => {
    if (!s || typeof s.key !== 'string') return;
    const name = typeof s.name === 'string' && s.name ? s.name : s.key;
    const ranks = [rankOf(normalizeForSearch(name), needle), rankOf(normalizeForSearch(s.key), needle)]
      .filter((r) => r !== null);
    if (ranks.length) ranked.push({ key: s.key, name, rank: Math.min(...ranks) });
  });
  return ranked
    .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
    .slice(0, SUPPLEMENT_SEARCH_LIMIT)
    .map(({ key, name }) => ({ key, name }));
};

// What an unmatched search may send to Mixpanel (design doc §5). The typed
// text is ruled to go (Aravind, 2026-10-13), but a name, email or phone never
// may (CLAUDE.md hard rule 1), and browsers autofill those into search boxes.
// So text that looks like contact details is not sent at all, and the rest is
// capped.
export const NOT_FOUND_QUERY_MAX = 60;
const PHONE_DIGITS = 7;

/** The text to record for a search with no match, or null to record nothing. */
export const notFoundQueryForAnalytics = (text) => {
  const query = String(text || '').trim();
  if (!query || query.includes('@')) return null;
  // 7 or more digits, in one run or split by spaces, dashes or dots.
  if ((query.match(/\d/g) || []).length >= PHONE_DIGITS) return null;
  return query.slice(0, NOT_FOUND_QUERY_MAX);
};
